/**
 * Chloe's Tables — Christmas / shipping-cutoff countdown.
 *
 * Two dates matter to a seasonal store, and they are not the same date:
 *   - the shipping cutoff, which is what actually drives the purchase
 *   - Christmas itself, which is the emotional anchor
 * The element counts to whichever is set, and once the cutoff passes it falls
 * back to Christmas so the banner never shows a dead zero.
 *
 * Dates are authored in the theme editor as plain ISO dates and interpreted in
 * the STORE's timezone, not the visitor's. A shopper in Tokyo and one in Denver
 * must see the same cutoff, because the cutoff is a fact about the warehouse.
 */

class MJCountdown extends HTMLElement {
  connectedCallback() {
    /* Daily mode: the sale "ends tonight" every night. Rather than an authored
       date that goes stale, the deadline is tonight's cutoff in the store's
       timezone, and #tick rolls it forward a day each time it lands. */
    this.daily = this.dataset.mode === 'daily';
    this.deadline = this.daily ? this.#nextDailyDeadline() : this.#parseDeadline();
    if (!this.deadline) return;

    this.parts = {
      days: this.querySelector('[data-days]'),
      hours: this.querySelector('[data-hours]'),
      minutes: this.querySelector('[data-minutes]'),
      seconds: this.querySelector('[data-seconds]'),
    };

    this.#tick();
    this.timer = setInterval(() => this.#tick(), 1000);

    // Long-lived tabs drift; resync on return rather than trusting the interval.
    this.onVisible = () => {
      if (!document.hidden) this.#tick();
    };
    document.addEventListener('visibilitychange', this.onVisible);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
    document.removeEventListener('visibilitychange', this.onVisible);
  }

  /**
   * Tonight's cutoff in the store's timezone, or tomorrow's once tonight has
   * already passed. Built off #parseDeadline so the timezone maths lives in
   * exactly one place.
   */
  #nextDailyDeadline() {
    const zone = this.dataset.timezone || 'America/New_York';
    const time = this.dataset.dailyTime || '23:59:59';

    let parts;
    try {
      const fmt = new Intl.DateTimeFormat('en-CA', {
        timeZone: zone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
      parts = fmt.format(new Date());
    } catch {
      parts = new Date().toISOString().slice(0, 10);
    }

    const previous = this.dataset.deadline;
    this.dataset.deadline = `${parts}T${time}`;
    let next = this.#parseDeadline();

    // Already past tonight's cutoff — roll to tomorrow.
    if (next && next - Date.now() <= 0) {
      const t = new Date(next.getTime() + 86400000);
      next = t;
    }
    if (previous !== undefined && !next) this.dataset.deadline = previous;
    return next;
  }

  /**
   * Builds a Date for the deadline in the store's timezone.
   *
   * `new Date('2026-12-18T23:59:59')` parses as the VISITOR's local time, which
   * would slide the cutoff by up to a day across timezones. To pin it to the
   * store, the wall-clock time is formatted back out of a UTC guess in the
   * store's zone and the residual offset is subtracted.
   */
  #parseDeadline() {
    const raw = this.dataset.deadline;
    const zone = this.dataset.timezone || 'America/New_York';
    if (!raw) return null;

    const [datePart, timePart = '23:59:59'] = raw.trim().split(/[T ]/);
    const [y, mo, d] = datePart.split('-').map(Number);
    const [h, mi, s] = timePart.split(':').map(Number);
    if (!y || !mo || !d) return null;

    const guess = Date.UTC(y, mo - 1, d, h || 0, mi || 0, s || 0);

    let offset;
    try {
      const fmt = new Intl.DateTimeFormat('en-US', {
        timeZone: zone,
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
      const p = Object.fromEntries(fmt.formatToParts(new Date(guess)).map((x) => [x.type, x.value]));
      const asZoned = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute, +p.second);
      offset = asZoned - guess;
    } catch {
      // Unknown timezone id — fall back to treating the input as UTC.
      offset = 0;
    }

    return new Date(guess - offset);
  }

  #tick() {
    let remaining = this.deadline - Date.now();

    if (remaining <= 0 && this.daily) {
      // Midnight rolled past: start the next night's countdown, never expire.
      this.deadline = new Date(this.deadline.getTime() + 86400000);
      remaining = this.deadline - Date.now();
    }

    if (remaining <= 0) {
      const fallback = this.dataset.fallbackDeadline;
      if (fallback && !this.switched) {
        this.switched = true;
        this.dataset.deadline = fallback;
        this.deadline = this.#parseDeadline();
        const label = this.querySelector('[data-label]');
        if (label && this.dataset.fallbackLabel) label.textContent = this.dataset.fallbackLabel;
        remaining = this.deadline - Date.now();
      } else {
        clearInterval(this.timer);
        this.setAttribute('expired', '');
        this.#paint(0, 0, 0, 0);
        return;
      }
    }

    const total = Math.floor(remaining / 1000);
    this.#paint(
      Math.floor(total / 86400),
      Math.floor((total % 86400) / 3600),
      Math.floor((total % 3600) / 60),
      total % 60
    );
  }

  #paint(days, hours, minutes, seconds) {
    const set = (node, value) => {
      if (!node) return;
      const text = String(value).padStart(2, '0');
      if (node.textContent !== text) node.textContent = text;
    };
    set(this.parts.days, days);
    set(this.parts.hours, hours);
    set(this.parts.minutes, minutes);
    set(this.parts.seconds, seconds);
  }
}

if (!customElements.get('ct-countdown')) {
  customElements.define('ct-countdown', MJCountdown);
}
